import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import { useI18n } from '../../i18n'

export type ScannerQualityAssessment = {
  scanner?: string | null
  target?: string | null
  session?: string | null
  spider?: string | null
  activeScan?: string | null
  passiveScan?: string | null
  resourcesCrawled?: number | null
  resourcesReturning404?: number | null
  staleResources?: number | null
  liveValidation?: string | null
  falsePositives?: number | null
  staleFindings?: number | null
  /** This-scan ACTIVE findings after verification — NOT org posture */
  thisScanActiveFindingCount?: number | null
  classifiedFindingTotal?: number | null
  /** @deprecated */
  currentFindings?: number | null
  /** @deprecated — do not show on quality card; use historicalContext */
  historicalFindings?: number | null
  priorAssessmentOccurrenceCount?: number | null
  /** Combined legacy badge */
  quality?: 'PASS' | 'WARNING' | 'FAIL' | string | null
  /** Scanner run health */
  executionQuality?: 'PASS' | 'WARNING' | 'FAIL' | string | null
  /** Finding trust */
  findingConfidence?: 'HIGH' | 'MEDIUM' | 'LOW' | 'REVIEW_REQUIRED' | string | null
  lowConfidence?: boolean
  recommendAsRemediationEvidence?: boolean
  reasons?: string[]
  executionReasons?: string[]
  findingReasons?: string[]
  contaminationRate?: number | null
}

function qualityBadgeClass(quality?: string | null) {
  const q = String(quality || '').toUpperCase()
  if (q === 'PASS' || q === 'HIGH') return entStyles.badgeStable
  if (q === 'WARNING' || q === 'MEDIUM') return entStyles.badgeWarn
  if (q === 'FAIL' || q === 'LOW' || q === 'REVIEW_REQUIRED') return entStyles.badgeRisk
  return entStyles.badge
}

function metric(value: number | null | undefined) {
  if (value == null) return '—'
  return value
}

type Props = {
  quality: ScannerQualityAssessment | null | undefined
}

/**
 * Compact Scanner Quality — execution/confidence only.
 * Does NOT repeat Scanner / Target / Pages Crawled / active finding counts
 * (those live in Assessment Summary).
 */
export default function ScannerQualityAssessmentCard({ quality }: Props) {
  const { t } = useI18n()
  if (!quality) return null

  const executionQuality = quality.executionQuality || quality.quality
  const findingConfidence = quality.findingConfidence || null
  const execUpper = String(executionQuality || '').toUpperCase()
  const showQualityWarning = execUpper && execUpper !== 'PASS'
  const resources404 = Number(quality.resourcesReturning404 ?? 0)

  return (
    <section className={entStyles.layer} data-testid="scanner-quality-assessment-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className={entStyles.layerLabel}>{t('reports.scannerQuality', 'Scanner Quality')}</p>
        <div className="flex flex-wrap gap-2">
          <span className={qualityBadgeClass(executionQuality)} data-testid="execution-quality-badge">
            {t('reports.executionQuality', 'Execution Quality')}: {executionQuality || 'UNKNOWN'}
          </span>
          {findingConfidence ? (
            <span className={qualityBadgeClass(findingConfidence)} data-testid="finding-confidence-badge">
              {t('reports.findingConfidence', 'Finding Confidence')}:{' '}
              {findingConfidence === 'REVIEW_REQUIRED'
                ? t('reports.findingConfidenceReview', 'REVIEW REQUIRED')
                : findingConfidence}
            </span>
          ) : null}
        </div>
      </div>
      <div className={entStyles.posturePanel}>
        <div className={entStyles.postureGrid}>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.session', 'Session')}</p>
            <p className={entStyles.postureMetricValue}>{quality.session || '—'}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.spider', 'Spider')}</p>
            <p className={entStyles.postureMetricValue}>{quality.spider || '—'}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>{t('reports.activeScan', 'Active Scan')}</p>
            <p className={entStyles.postureMetricValue}>{quality.activeScan || '—'}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.liveValidation', 'Live Validation')}
            </p>
            <p className={entStyles.postureMetricValue}>{quality.liveValidation || '—'}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.staleResources', 'Stale Resources')}
            </p>
            <p className={entStyles.postureMetricValue}>{metric(quality.staleResources)}</p>
          </div>
          <div className={entStyles.postureMetric}>
            <p className={entStyles.postureMetricLabel}>
              {t('reports.falsePositives', 'False Positives')}
            </p>
            <p className={entStyles.postureMetricValue}>{metric(quality.falsePositives)}</p>
          </div>
          {resources404 > 0 ? (
            <div className={entStyles.postureMetric}>
              <p className={entStyles.postureMetricLabel}>
                {t('reports.resources404', '404 resources')}
              </p>
              <p className={entStyles.postureMetricValue}>{resources404}</p>
            </div>
          ) : null}
        </div>

        {executionQuality === 'PASS' &&
        (findingConfidence === 'REVIEW_REQUIRED' || findingConfidence === 'LOW') ? (
          <p className={entStyles.postureStory} style={{ marginTop: '0.75rem' }} data-testid="finding-review-note">
            {t(
              'reports.executionPassFindingReview',
              'Execution Quality: PASS. Finding confidence requires review — many findings were classified as false-positive or stale. The scanner itself completed successfully.',
            )}
            {quality.findingReasons?.length ? ` ${quality.findingReasons.join(' · ')}` : ''}
          </p>
        ) : null}
        {showQualityWarning ? (
          <p className={entStyles.postureStory} style={{ marginTop: '0.75rem' }} data-testid="quality-warning">
            {quality.lowConfidence
              ? t(
                  'reports.lowConfidenceScan',
                  'Low confidence scan — do not use as evidence of remediation until quality issues are resolved.',
                )
              : t(
                  'reports.qualityNotPass',
                  'Scanner quality requires attention before using this assessment as remediation evidence.',
                )}
            {quality.reasons?.length ? ` ${quality.reasons.join(' · ')}` : ''}
          </p>
        ) : null}
      </div>
    </section>
  )
}
